import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Award, ChevronRight, MessageSquare } from "lucide-react";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
}

/**
 * Compact horizontal strip of moderator-pinned mentorship threads for a given
 * area (nutrition / training / peptides). Hides itself when empty.
 */
export default function MentorshipStrip({ topic = "nutrition", label = "Mentorship" }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/community/mentorship", { params: { topic, limit: 4 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, [topic]);

  if (!loaded || items.length === 0) return null;

  return (
    <div className="mb-6" data-testid={`mentorship-strip-${topic}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2 font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488]">
          <Award className="w-3.5 h-3.5" /> // {label} · {topic}
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-400">
          Pinned by staff
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((t) => (
          <Link
            key={t.id}
            to={`/community/${t.id}`}
            data-testid={`mentorship-thread-${t.id}`}
            className="block border border-[#0D9488]/25 bg-[#0D9488]/[0.03] hover:border-[#0D9488] hover:bg-[#0D9488]/10 transition-colors p-4 group"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-white font-semibold text-[15px] leading-snug line-clamp-2 group-hover:text-[#0D9488] transition-colors">
                  {t.title}
                </div>
                <div className="mt-1 flex items-center gap-3 font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                  <span>{t.author_name}</span>
                  {t.author_role !== "user" && (
                    <span className="text-[#0D9488]">// {t.author_role}</span>
                  )}
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.reply_count}</span>
                  <span>{fmtDate(t.last_reply_at || t.created_at)}</span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-[#0D9488] transition-colors shrink-0 mt-1" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
